import React from 'react';
import { Typography } from '@mui/material';

/**
 * Component to safely render HTML content from question text
 * Supports common formatting tags like <b>, <i>, <u>, <ul>, <li>, etc.
 */
const HtmlRenderer = ({ 
  content, 
  component = "span", 
  sx = {},
  ...props 
}) => {
  // If no content, return empty
  if (!content) return null;

  // Decode HTML entities (handles &lt; &gt; &amp; etc)
  const decodeHtmlEntities = (text) => {
    if (typeof document === 'undefined') return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Clean and sanitize the HTML content
  const sanitizeHtml = (html) => {
    // First decode any HTML entities
    let decoded = decodeHtmlEntities(html);
    
    // Allow only safe HTML tags that are commonly used in questions
    const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'ul', 'li', 'ol', 'br', 'p', 'span', 'div'];
    
    // Simple sanitization - remove any tags not in allowed list
    // This is a basic implementation. For production, consider using a library like DOMPurify
    let sanitized = decoded;
    
    // Remove script tags and other dangerous elements
    sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, '');
    sanitized = sanitized.replace(/<iframe[^>]*>.*?<\/iframe>/gi, '');
    sanitized = sanitized.replace(/on\w+="[^"]*"/gi, ''); // Remove event handlers
    
    return sanitized;
  };

  const sanitizedContent = sanitizeHtml(content);

  return (
    <Typography
      component={component}
      sx={{
        '& b, & strong': {
          fontWeight: 700,
        },
        '& i, & em': {
          fontStyle: 'italic',
        },
        '& u': {
          textDecoration: 'underline',
        },
        '& ul': {
          margin: '8px 0',
          paddingLeft: '20px',
        },
        '& ol': {
          margin: '8px 0',
          paddingLeft: '20px',
        },
        '& li': {
          margin: '4px 0',
          listStyleType: 'disc',
        },
        '& p': {
          margin: '8px 0',
          '&:first-of-type': {
            marginTop: 0,
          },
          '&:last-of-type': {
            marginBottom: 0,
          },
        },
        '& br': {
          content: '""',
          display: 'block',
          marginBottom: '4px',
        },
        ...sx
      }}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      {...props}
    />
  );
};

export default HtmlRenderer;
